import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { createApiUrlWithPrefix } from '../config/api';
import { 
  Calendar,
  Clock,
  Users,
  Plus,
  Edit,
  Trash2,
  XCircle,
  UserPlus,
  Check,
  Search,
  LayoutList,
  UserCheck
} from 'lucide-react';
import CalendarView from './CalendarView';

import { API_BASE_URL } from '../config/api';
function SimplifiedSessionManagement() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly', 'daily', or 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [horseSearchTerm, setHorseSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [stableFilter, setStableFilter] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    stable: '',
    target: '',
    level: '',
    type: '',
    weeklyDays: [],
    selectedDates: [],
    date: '',
    startTime: '',
    endTime: '',
    maxParticipants: 10,
    useSlots: false,
    slotDurationMinutes: 20,
    slotPauseMinutes: 5,
    selectedHorses: []
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showHorsesModal, setShowHorsesModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [horses, setHorses] = useState([]);
  const [sessionHorses, setSessionHorses] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [sessionSlots, setSessionSlots] = useState([]);
  const [participantModalTab, setParticipantModalTab] = useState('assign');
  const [assignUserTab, setAssignUserTab] = useState('riders');
  const [participantSearch, setParticipantSearch] = useState('');
  const [dailyDateDraft, setDailyDateDraft] = useState('');
  const [horseModalTab, setHorseModalTab] = useState('assign');

    const [refreshKey, setRefreshKey] = useState(0);
const stables = [
    { value: '1', label: t('sessions.simplified.form.stable1') },
    { value: '2', label: t('sessions.simplified.form.stable2') },
    { value: '3', label: t('sessions.simplified.form.stable3') }
  ];

  const sessionTargets = [
    { value: 'Rider', label: t('sessions.simplified.form.riders') },
    { value: 'Owner', label: t('sessions.simplified.form.owners') }
  ];

  const riderLevels = [
    { value: 'Beginner', label: t('sessions.simplified.form.beginner') },
    { value: 'Intermediate', label: t('sessions.simplified.form.intermediate') },
    { value: 'Professional', label: t('sessions.simplified.form.professional') }
  ];

  const sessionTypes = [
    { value: 'Weekly', label: t('sessions.simplified.form.weekly') },
    { value: 'Daily', label: t('sessions.simplified.form.daily') }
  ];

  const weeklyDays = [
    { value: 'Monday', label: t('sessions.daysOfWeek.monday') },
    { value: 'Tuesday', label: t('sessions.daysOfWeek.tuesday') },
    { value: 'Wednesday', label: t('sessions.daysOfWeek.wednesday') },
    { value: 'Thursday', label: t('sessions.daysOfWeek.thursday') },
    { value: 'Friday', label: t('sessions.daysOfWeek.friday') },
    { value: 'Saturday', label: t('sessions.daysOfWeek.saturday') },
    { value: 'Sunday', label: t('sessions.daysOfWeek.sunday') }
  ];

  const calculateSlotCount = (startTime, endTime, durationMinutes, pauseMinutes) => {
    if (!startTime || !endTime || !durationMinutes) return 0;
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    let current = startHour * 60 + startMinute;
    const end = endHour * 60 + endMinute;
    let count = 0;

    while (true) {
      const slotEnd = current + durationMinutes;
      if (slotEnd > end) break;
      count += 1;
      current = slotEnd + (pauseMinutes || 0);
      if (current >= end) break;
    }

    return count;
  };

  const buildSlotSessionPayload = (baseData) => ({
    ...baseData,
    useSlots: formData.useSlots,
    slotDurationMinutes: formData.useSlots ? formData.slotDurationMinutes : null,
    slotPauseMinutes: formData.useSlots ? formData.slotPauseMinutes : null,
  });

  const sessionUsesSlots = (session) => !!(session?.useSlots || session?.UseSlots);

  const isIsoDateList = (value) => {
    if (!value || typeof value !== 'string') return false;
    const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
    return parts.length > 0 && parts.every((p) => /^\d{4}-\d{2}-\d{2}/.test(p));
  };

  const parseDailyDatesFromSession = (session) => {
    if (session?.weeklyDays && isIsoDateList(session.weeklyDays)) {
      return session.weeklyDays.split(',').map((s) => s.trim()).filter(Boolean).sort();
    }
    if (session?.date) {
      return [session.date.split('T')[0]];
    }
    return [];
  };

  const buildDailySessionSchedule = (selectedDates) => {
    const sorted = [...selectedDates].sort();
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const [y, m, d] = last.split('-').map(Number);
    return {
      date: new Date(`${first}T12:00:00Z`).toISOString(),
      weeklyDays: sorted.join(','),
      expiresAt: new Date(Date.UTC(y, m - 1, d, 23, 59, 59)).toISOString()
    };
  };

  const addDailyDate = () => {
    if (!dailyDateDraft) {
      toast.error('Pick a date first');
      return;
    }
    if ((formData.selectedDates || []).includes(dailyDateDraft)) {
      toast.error('This date is already selected');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      selectedDates: [...(prev.selectedDates || []), dailyDateDraft].sort()
    }));
    setDailyDateDraft('');
  };

  const removeDailyDate = (dateStr) => {
    setFormData((prev) => ({
      ...prev,
      selectedDates: (prev.selectedDates || []).filter((d) => d !== dateStr)
    }));
  };

  const getSlotId = (slot) => slot?.id ?? slot?.Id;
  const getSlotIndex = (slot) => slot?.slotIndex ?? slot?.SlotIndex ?? 0;
  const getSlotMax = (slot) => slot?.maxParticipants ?? slot?.MaxParticipants ?? 0;
  const getSlotCurrent = (slot) => slot?.currentParticipants ?? slot?.CurrentParticipants ?? 0;
  const isSlotFull = (slot) => slot?.isFull ?? slot?.IsFull ?? getSlotCurrent(slot) >= getSlotMax(slot);

  const formatSlotTime = (timeValue) => {
    if (!timeValue) return '--:--';
    const raw = String(timeValue);
    if (raw.includes('.')) {
      const parts = raw.split('.');
      return parts.length >= 3 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : raw.substring(0, 5);
    }
    return raw.substring(0, 5);
  };

  const getSelectedSlot = () => sessionSlots.find((s) => getSlotId(s) === selectedSlotId);

  const filterUsersBySearch = (users) => {
    if (!participantSearch.trim()) return users;
    const query = participantSearch.toLowerCase();
    return users.filter((user) =>
      `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query)
    );
  };

  const getRoleLabel = (role) => {
    if (role === 0) return 'Rider';
    if (role === 1) return 'Owner';
    if (role === 2) return 'Coach';
    if (role === 5) return 'Temporary';
    return 'User';
  };

  const getAssignableUsersForTab = () => {
    if (assignUserTab === 'coaches') return filterUsersBySearch(getCoaches());
    if (assignUserTab === 'temporary') return filterUsersBySearch(getTemporaryUsers());
    return filterUsersBySearch(getTargetUsers());
  };

  const canAssignUsers = () => {
    if (selectedUsers.length === 0) return false;
    if (sessionUsesSlots(selectedSession)) {
      const hasNonCoach = selectedUsers.some((u) => u.role !== 2);
      if (hasNonCoach && !selectedSlotId) return false;
    }
    return true;
  };

  const getParticipantAccent = (tab) => {
    if (tab === 'coaches') return 'purple';
    if (tab === 'temporary') return 'orange';
    return 'blue';
  };

  const getParticipantSlotId = (participant) =>
    participant?.sessionSlotId ?? participant?.SessionSlotId ?? null;

  const refreshParticipantsModal = async (sessionId, keepSelectedSlot = true) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };

    const sessionResponse = await axios.get(createApiUrlWithPrefix(`simplifiedsessions/${sessionId}`), { headers });
    const sessionData = sessionResponse.data;
    setSelectedSession(sessionData);

    if (sessionUsesSlots(sessionData)) {
      const slotsResponse = await axios.get(createApiUrlWithPrefix(`simplifiedsessions/${sessionId}/slots`), { headers });
      const slots = slotsResponse.data || [];
      setSessionSlots(slots);

      if (!keepSelectedSlot || !selectedSlotId) {
        const firstAvailable = slots.find((slot) => !isSlotFull(slot)) || slots[0];
        setSelectedSlotId(firstAvailable ? getSlotId(firstAvailable) : null);
      } else {
        const stillExists = slots.some((slot) => getSlotId(slot) === selectedSlotId);
        if (!stillExists && slots.length > 0) {
          setSelectedSlotId(getSlotId(slots[0]));
        }
      }
    } else {
      setSessionSlots([]);
      setSelectedSlotId(null);
    }

    await fetchSessions();
  };

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (typeFilter) {
        params.append('type', typeFilter);
      } else if (viewMode === 'daily') {
        params.append('type', 'Daily');
      } else if (viewMode === 'weekly') {
        params.append('type', 'Weekly');
      }
      if (targetFilter) params.append('target', targetFilter);
      if (stableFilter) params.append('stable', stableFilter);
      
      const response = await axios.get(createApiUrlWithPrefix(`simplifiedsessions?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Extract sessions from the paginated response
      const sessionsData = response.data.sessions || response.data;
      
      setSessions(sessionsData);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems ?? sessionsData.length);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [viewMode, currentPage, itemsPerPage, searchTerm, typeFilter, targetFilter, stableFilter]);

  const fetchHorses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/horses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // Handle paginated response - extract horses array from response.data.horses
      const horsesData = response.data?.horses || response.data || [];
      const horsesArray = Array.isArray(horsesData) ? horsesData : [];
      setHorses(horsesArray);
      console.log('Fetched horses:', horsesArray.length, 'horses');
    } catch (error) {
      console.error('Failed to fetch horses:', error);
      toast.error('Failed to load horses');
      // Ensure we set an empty array on error
      setHorses([]);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchHorses();
  }, [fetchSessions, fetchHorses]);

  const handleCreateSession = async () => {
    if (!formData.name || !formData.stable || !formData.target || !formData.startTime || !formData.endTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.target === 'Rider' && !formData.level) {
      toast.error('Please select a rider level');
      return;
    }

    if (formData.type === 'Weekly' && (formData.weeklyDays || []).length === 0) {
      toast.error('Please select at least one day for weekly sessions');
      return;
    }
    
    if (formData.type === 'Daily' && (formData.selectedDates || []).length === 0) {
      toast.error('Please select at least one date for daily sessions');
      return;
    }
    
    if (formData.useSlots && (!formData.slotDurationMinutes || formData.slotDurationMinutes <= 0)) {
      toast.error('Please set a valid slot duration');
      return;
    }

    if (formData.useSlots && calculateSlotCount(formData.startTime, formData.endTime, formData.slotDurationMinutes, formData.slotPauseMinutes) === 0) {
      toast.error('No slots fit in this time range. Adjust start/end time or slot settings.');
      return;
    }
    
    try {
      // For weekly sessions, create one session with all selected days
      if (formData.type === 'Weekly') {
        console.log('Creating weekly session for days:', formData.weeklyDays);
        
        // Get current user ID with validation
        const currentUserId = localStorage.getItem('userId');
        if (!currentUserId) {
          toast.error('User session expired. Please login again.');
          return;
        }
        
        // Create one session with the first selected day as the primary day
        const primaryDay = (formData.weeklyDays || [])[0];
          const sessionData = buildSlotSessionPayload({
          name: formData.name,
          stable: formData.stable,
          date: getNextDateForDay(primaryDay), // Get next occurrence of the first day
            startTime: formData.startTime + ':00',
            endTime: formData.endTime + ':00',
          level: formData.target === 'Rider' ? getLevelValue(formData.level) : 0, // Default for owners
          type: getTypeValue(formData.type),
          target: getTargetValue(formData.target),
          maxParticipants: formData.maxParticipants,
          weeklyDay: getDayValue(primaryDay), // Store the first day as primary
          weeklyDays: (formData.weeklyDays || []).join(','), // Store all days as comma-separated string
          createdBy: parseInt(currentUserId), // Use actual logged-in user ID
          expiresAt: null,
          horseIds: formData.selectedHorses || []
        });

        await axios.post(`${API_BASE_URL}/api/simplifiedsessions`, sessionData, {
            headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
        console.log('Successfully created weekly session for days:', formData.weeklyDays);
      } else {
        // For daily sessions, create one session
        // Get current user ID with validation
        const currentUserId = localStorage.getItem('userId');
        if (!currentUserId) {
          toast.error('User session expired. Please login again.');
          return;
        }

        const dailySchedule = buildDailySessionSchedule(formData.selectedDates);
        const sessionData = buildSlotSessionPayload({
          name: formData.name,
          stable: formData.stable,
          date: dailySchedule.date,
          startTime: formData.startTime + ':00',
          endTime: formData.endTime + ':00',
          level: formData.target === 'Rider' ? getLevelValue(formData.level) : 0,
          type: getTypeValue(formData.type),
          target: getTargetValue(formData.target),
          maxParticipants: formData.maxParticipants,
          weeklyDay: null,
          weeklyDays: dailySchedule.weeklyDays,
          createdBy: parseInt(currentUserId),
          expiresAt: dailySchedule.expiresAt,
          horseIds: formData.selectedHorses || []
        });

        await axios.post(`${API_BASE_URL}/api/simplifiedsessions`, sessionData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      toast.success('Session(s) created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchSessions();
      setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create session');
    }
  };

  const handleEditSession = async () => {
    if (!formData.name || !formData.stable || !formData.target || !formData.startTime || !formData.endTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.target === 'Rider' && !formData.level) {
      toast.error('Please select a rider level');
      return;
    }

    if (formData.type === 'Weekly' && (formData.weeklyDays || []).length === 0) {
      toast.error('Please select at least one day for weekly sessions');
      return;
    }
    
    if (formData.type === 'Daily' && (formData.selectedDates || []).length === 0) {
      toast.error('Please select at least one date for daily sessions');
      return;
    }
    
    try {
      const dailySchedule = formData.type === 'Daily'
        ? buildDailySessionSchedule(formData.selectedDates)
        : null;
      const updateData = buildSlotSessionPayload({
        name: formData.name,
        stable: formData.stable,
        date: formData.type === 'Daily'
          ? dailySchedule.date
          : getNextDateForDay((formData.weeklyDays || [])[0]),
        startTime: formData.startTime + ':00',
        endTime: formData.endTime + ':00',
        level: formData.target === 'Rider' ? getLevelValue(formData.level) : 0,
        type: getTypeValue(formData.type),
        target: getTargetValue(formData.target),
        maxParticipants: formData.maxParticipants,
        weeklyDay: formData.type === 'Weekly' ? getDayValue((formData.weeklyDays || [])[0]) : null,
        weeklyDays: formData.type === 'Weekly'
          ? (formData.weeklyDays || []).join(',')
          : dailySchedule.weeklyDays,
        expiresAt: formData.type === 'Daily' ? dailySchedule.expiresAt : null
      });

      await axios.put(createApiUrlWithPrefix(`simplifiedsessions/${editingSession.id}`), updateData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Session updated successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error updating session:', error);
      toast.error('Failed to update session');
    }
  };

  // Helper function to get next date for a specific day of the week
  const getNextDateForDay = (dayName) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const targetDay = days.indexOf(dayName);
    const today = new Date();
    const currentDay = today.getDay();
    
    let daysUntilTarget = (targetDay - currentDay + 7) % 7;
    if (daysUntilTarget === 0) daysUntilTarget = 7; // Next week if today is the target day
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilTarget);
    return nextDate.toISOString();
  };

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Helper function to convert level string to integer
  const getLevelValue = (level) => {
    switch (level) {
      case 'Beginner': return 0;
      case 'Intermediate': return 1;
      case 'Professional': return 2;
      default: return 0;
    }
  };

  // Helper function to convert type string to integer
  const getTypeValue = (type) => {
    switch (type) {
      case 'Weekly': return 0;
      case 'Daily': return 1;
      default: return 0;
    }
  };

  // Helper function to convert target string to integer
  const getTargetValue = (target) => {
    switch (target) {
      case 'Rider': return 0;
      case 'Owner': return 1;
      default: return 0;
    }
  };

  // Helper function to convert day string to integer
  const getDayValue = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days.indexOf(day);
  };

  // Helper functions to convert integers back to strings for editing
  const getLevelName = (level) => {
    switch (level) {
      case 0: return 'Beginner';
      case 1: return 'Intermediate';
      case 2: return 'Professional';
      default: return 'Beginner';
    }
  };

  const getTypeName = (type) => {
    switch (type) {
      case 0: return 'Weekly';
      case 1: return 'Daily';
      default: return 'Weekly';
    }
  };

  const getTargetName = (target) => {
    switch (target) {
      case 0: return 'Rider';
      case 1: return 'Owner';
      default: return 'Rider';
    }
  };

  const getDayName = (dayValue) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayValue] || 'Monday';
  };


  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await axios.delete(createApiUrlWithPrefix(`simplifiedsessions/${sessionId}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
        toast.success('Session deleted successfully!');
        fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error deleting session:', error);
        toast.error('Failed to delete session');
      }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    
    let weeklyDaysArray = [];
    if (session.weeklyDays && !isIsoDateList(session.weeklyDays)) {
      weeklyDaysArray = session.weeklyDays.split(',').map(day => day.trim());
    } else if (session.type === 0 && session.weeklyDay !== null) {
      // Fallback to single day if WeeklyDays is not available
      weeklyDaysArray = [getDayName(session.weeklyDay)];
    }
    
    const sessionTypeName = getTypeName(session.type);
    const dailyDates = sessionTypeName === 'Daily' ? parseDailyDatesFromSession(session) : [];

    setFormData({
      name: session.name || '',
      stable: session.stable || '1',
      date: session.date.split('T')[0],
      startTime: session.startTime.substring(0, 5),
      endTime: session.endTime.substring(0, 5),
      level: getLevelName(session.level),
      type: sessionTypeName,
      target: getTargetName(session.target),
      maxParticipants: session.maxParticipants || 10,
      useSlots: session.useSlots || false,
      slotDurationMinutes: session.slotDurationMinutes || 20,
      slotPauseMinutes: session.slotPauseMinutes || 5,
      weeklyDays: sessionTypeName === 'Weekly' ? weeklyDaysArray : [],
      weeklyDay: weeklyDaysArray.length > 0 ? weeklyDaysArray[0] : '',
      selectedDates: dailyDates,
      selectedHorses: []
    });
    setDailyDateDraft('');
    setCurrentStep(1);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      stable: '',
      target: '',
      level: '',
      type: '',
      weeklyDays: [],
      selectedDates: [],
      date: '',
      startTime: '',
      endTime: '',
      maxParticipants: 10,
      useSlots: false,
      slotDurationMinutes: 20,
      slotPauseMinutes: 5,
      selectedHorses: []
    });
    setDailyDateDraft('');
    setCurrentStep(1);
    setEditingSession(null);
    setSelectedSlotId(null);
    setSessionSlots([]);
  };

  // Participant management functions
  const handleManageParticipants = async (session) => {
    setShowParticipantsModal(true);
    setSelectedUsers([]);
    setSelectedSlotId(null);
    setSessionSlots([]);
    setParticipantModalTab('assign');
    setAssignUserTab('riders');
    setParticipantSearch('');
    setSelectedSession(session);
    await fetchAvailableUsers();

    try {
      await refreshParticipantsModal(session.id, false);
    } catch (error) {
      console.error('Error loading session participants:', error);
      toast.error('Failed to load session details');
    }
  };

  // Filter users based on session target
  const getTargetUsers = () => {
    if (!selectedSession || !availableUsers) return [];
    
    
    const targetRole = selectedSession.target === 0 ? 0 : 1; // 0 = Rider, 1 = Owner
    
    return availableUsers.filter(user => {
      const isAlreadyAssigned = sessionUsesSlots(selectedSession)
        ? selectedSession.participants?.some(
            (p) => p.user?.id === user.id && getParticipantSlotId(p) === selectedSlotId
          )
        : selectedSession.participants?.some((p) => p.user?.id === user.id);
      if (isAlreadyAssigned) return false;
      
      
      // For riders, filter by level (only for rider sessions)
      if (user.role === 0 && selectedSession.target === 0) { // Rider and rider session
        // For regular riders, check level match
        const sessionLevel = selectedSession.level ?? 0; // Default to Beginner if null
        const userLevel = user.level ?? 0; // Default to Beginner if null
        
        // Map session level to user level
        // Session levels: 0=Beginner, 1=Intermediate, 2=Professional
        // User levels: 0=Beginner, 1=Intermediate, 2=Professional
        return userLevel === sessionLevel;
      }
      
      // Exclude temporary users from riders section (they have their own section)
      if (user.role === 5) return false;
      
      // For riders in non-rider sessions, or riders that don't match level, exclude them
      if (user.role === 0) return false;
      
      // For owners, no level filtering needed
      return user.role === targetRole;
    });
  };

  // Filter coaches
  const getCoaches = () => {
    if (!selectedSession || !availableUsers) return [];
    
    const coachRole = 2; // Coach role
    
    return availableUsers.filter(user => 
      user.role === coachRole && 
      !selectedSession.participants?.some(p => p.user.id === user.id)
    );
  };

  // Filter temporary users
  const getTemporaryUsers = () => {
    if (!selectedSession || !availableUsers) return [];
    
    const temporaryRole = 5; // Temporary user role
    
    return availableUsers.filter(user => 
      user.role === temporaryRole && 
      !selectedSession.participants?.some(p => p.user.id === user.id)
    );
  };

  // Get participant count excluding coaches
  const getParticipantCount = (session) => {
    if (session?.currentParticipants != null) return session.currentParticipants;
    if (!session?.participants) return 0;
    return session.participants.filter((participant) => participant.user?.role !== 2).length;
  };

  const getSessionTotalCapacity = (session) => {
    if (session?.totalCapacity > 0) return session.totalCapacity;
    if (sessionUsesSlots(session)) {
      const slots = session.slots || session.Slots || [];
      if (slots.length > 0) {
        return slots.reduce(
          (sum, slot) => sum + (slot.maxParticipants ?? slot.MaxParticipants ?? session.maxParticipants ?? 0),
          0
        );
      }
      const slotCount = session.totalSlots ?? session.TotalSlots ?? 0;
      if (slotCount > 0 && session.maxParticipants) {
        return slotCount * session.maxParticipants;
      }
    }
    return session.maxParticipants || 0;
  };

  const formatSessionParticipantLabel = (session) => {
    const count = getParticipantCount(session);
    const capacity = getSessionTotalCapacity(session);
    if (capacity > 0) {
      return `${count}/${capacity} ${t('sessions.participants')}`;
    }
    return `${count} ${t('sessions.participants')}`;
  };

  // Get coaches in current session
  const getCoachesInSession = () => {
    if (!selectedSession?.participants) return [];
    
    return selectedSession.participants.filter(participant => participant.user.role === 2);
  };

  // Get participants in current session (excluding coaches)
  const getParticipantsInSession = () => {
    if (!selectedSession?.participants) return [];

    const nonCoaches = selectedSession.participants.filter((participant) => participant.user.role !== 2);

    if (sessionUsesSlots(selectedSession) && selectedSlotId) {
      return nonCoaches.filter((participant) => getParticipantSlotId(participant) === selectedSlotId);
    }

    return nonCoaches;
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleUserSelectClick = (user) => {
    if (sessionUsesSlots(selectedSession) && user.role !== 2 && !selectedSlotId) {
      toast.error('Select a slot at the top first');
      return;
    }
    handleUserSelect(user);
  };

  // Handle confirmation
  const handleConfirmAssignments = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    const selectedParticipants = selectedUsers.filter(user => user.role !== 2);
    const selectedCoaches = selectedUsers.filter(user => user.role === 2);

    if (sessionUsesSlots(selectedSession) && selectedParticipants.length > 0 && !selectedSlotId) {
      toast.error('Please select a slot first');
      return;
    }

    // Check if session has enough capacity for all selected users (excluding coaches)
    const currentParticipants = getParticipantsInSession().length;
    const maxParticipants = selectedSession.maxParticipants;
    
    if (sessionUsesSlots(selectedSession) && selectedParticipants.length > 0) {
      if (!selectedSlotId) {
        toast.error('Please select a slot first');
        return;
      }

      const slot = sessionSlots.find((s) => getSlotId(s) === selectedSlotId);
      const slotCurrent = slot ? getSlotCurrent(slot) : getParticipantsInSession().length;
      const slotMax = slot ? getSlotMax(slot) : selectedSession.maxParticipants;

      if (slotMax && slotCurrent + selectedParticipants.length > slotMax) {
        const availableSpots = slotMax - slotCurrent;
        toast.error(`Slot capacity exceeded! Only ${availableSpots} spot(s) left in this slot.`);
        return;
      }
    } else if (maxParticipants && (currentParticipants + selectedParticipants.length) > maxParticipants) {
      const availableSpots = maxParticipants - currentParticipants;
      toast.error(`Session capacity exceeded! Only ${availableSpots} spot(s) available, but ${selectedParticipants.length} participant(s) selected.`);
      return;
    }

    const participantCount = selectedParticipants.length;
    const coachCount = selectedCoaches.length;

    let failedCount = 0;
    for (const user of selectedUsers) {
      const ok = await handleAssignParticipant(user.id, { silent: true });
      if (!ok) failedCount += 1;
    }

    setSelectedUsers([]);

    if (failedCount > 0) {
      toast.error(`Failed to assign ${failedCount} user(s). Others may have been saved.`);
      return;
    }

    if (participantCount > 0 && coachCount > 0) {
      toast.success(`Successfully assigned ${participantCount} participant(s) and ${coachCount} coach(es)`);
    } else if (participantCount > 0) {
      toast.success(`Successfully assigned ${participantCount} participant(s)`);
    } else if (coachCount > 0) {
      toast.success(`Successfully assigned ${coachCount} coach(es)`);
    }
  };

  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users?limit=10000`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      // The API returns { users: [...] } format
      const users = response.data?.users || [];
      setAvailableUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setAvailableUsers([]); // Set empty array on error
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignParticipant = async (userId, options = { silent: false }) => {
    if (!selectedSession) return false;

    const user = availableUsers.find((u) => u.id === userId)
      || selectedUsers.find((u) => u.id === userId)
      || selectedSession.participants?.find((p) => p.user?.id === userId)?.user;
    const isCoach = user?.role === 2;

    if (sessionUsesSlots(selectedSession) && !isCoach && !selectedSlotId) {
      if (!options.silent) toast.error('Please select a slot before assigning participants');
      return false;
    }
    
    if (sessionUsesSlots(selectedSession) && !isCoach) {
      const slot = sessionSlots.find((s) => getSlotId(s) === selectedSlotId);
      if (slot && isSlotFull(slot)) {
        if (!options.silent) toast.error(`Slot ${getSlotIndex(slot) + 1} is full (${getSlotMax(slot)} max)`);
        return false;
      }
    } else if (!sessionUsesSlots(selectedSession) && !isCoach) {
      const currentParticipants = getParticipantsInSession().length;
      const maxParticipants = selectedSession.maxParticipants;
      
      if (maxParticipants && currentParticipants >= maxParticipants) {
        if (!options.silent) toast.error(`Session is full! Maximum capacity is ${maxParticipants} participants.`);
        return false;
      }
    }
    
    try {
      await axios.post(createApiUrlWithPrefix(`simplifiedsessions/${selectedSession.id}/participants`), {
        userId: userId,
        sessionSlotId: sessionUsesSlots(selectedSession) && !isCoach ? selectedSlotId : null
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!options.silent) toast.success(isCoach ? 'Coach assigned successfully!' : 'Participant assigned successfully!');
      await refreshParticipantsModal(selectedSession.id, true);
      return true;
    } catch (error) {
      console.error('Error assigning participant:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to assign participant';
      toast.error(message);
      return false;
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    if (!selectedSession) return;
    
    try {
      await axios.delete(createApiUrlWithPrefix(`simplifiedsessions/${selectedSession.id}/participants/${participantId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Participant removed successfully!');
      await refreshParticipantsModal(selectedSession.id, true);
    } catch (error) {
      console.error('Error removing participant:', error);
      toast.error('Failed to remove participant');
    }
  };

  // Horse management functions
  const handleManageHorses = async (session) => {
    console.log('Opening horse management for session:', session.id);
    setSelectedSession(session);
    setHorseSearchTerm('');
    setHorseModalTab('assign');
    setShowHorsesModal(true);
    // Fetch both session horses and all available horses
    await Promise.all([
      fetchSessionHorses(session.id),
      fetchHorses()
    ]);
    console.log('Horse management data loaded');
  };

  const fetchSessionHorses = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(createApiUrlWithPrefix(`sessions/${sessionId}/horses`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // Ensure we always set an array
      const sessionHorsesData = Array.isArray(response.data) ? response.data : [];
      setSessionHorses(sessionHorsesData);
      console.log('Fetched session horses:', sessionHorsesData.length, 'horses for session', sessionId);
    } catch (error) {
      console.error('Failed to fetch session horses:', error);
      setSessionHorses([]);
    }
  };

  const assignHorseToSession = async (sessionId, horseId) => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      await axios.post(createApiUrlWithPrefix(`sessions/${sessionId}/assign-horse`), {
        horseId,
        assignedByUserId: userId ? parseInt(userId) : null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Horse assigned to session successfully');
      await fetchSessionHorses(sessionId);
      await fetchSessions(); // Refresh the main session list
    } catch (error) {
      console.error('Failed to assign horse:', error);
      toast.error('Failed to assign horse to session');
    }
  };

  const removeHorseFromSession = async (sessionId, horseId) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(createApiUrlWithPrefix(`sessions/${sessionId}/remove-horse/${horseId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Horse removed from session successfully');
      await fetchSessionHorses(sessionId);
      await fetchSessions(); // Refresh the main session list
    } catch (error) {
      console.error('Failed to remove horse:', error);
      toast.error('Failed to remove horse from session');
    }
  };

  const wizardStepConfig = [
    { step: 1, label: t('sessions.sessionDetails') },
    { step: 2, label: t('sessions.whoIsThisSessionFor') },
    { step: 3, label: 'Rider Level', riderOnly: true },
    { step: 4, label: 'Session Type' },
    { step: 5, label: 'Schedule' },
    { step: 6, label: 'Time & Capacity' }
  ];

  const getActiveWizardSteps = () =>
    wizardStepConfig.filter((s) => !s.riderOnly || formData.target === 'Rider');

  const getCurrentWizardMeta = () => {
    const steps = getActiveWizardSteps();
    const index = steps.findIndex((s) => s.step === currentStep);
    return {
      steps,
      index: index >= 0 ? index : 0,
      title: steps.find((s) => s.step === currentStep)?.label || ''
    };
  };

  const isLastWizardStep = () => {
    const steps = getActiveWizardSteps();
    return steps[steps.length - 1]?.step === currentStep;
  };

  const nextStep = () => {
    const steps = getActiveWizardSteps();
    const idx = steps.findIndex((s) => s.step === currentStep);
    if (idx >= 0 && idx < steps.length - 1) {
      setCurrentStep(steps[idx + 1].step);
    }
  };

  const prevStep = () => {
    const steps = getActiveWizardSteps();
    const idx = steps.findIndex((s) => s.step === currentStep);
    if (idx > 0) {
      setCurrentStep(steps[idx - 1].step);
    }
  };

  const getAvailableSessionHorses = () => {
    if (!Array.isArray(horses) || !Array.isArray(sessionHorses)) return [];
    const query = horseSearchTerm.trim().toLowerCase();
    return horses
      .filter((horse) => !sessionHorses.some((sh) => sh.horseId === horse.id))
      .filter(
        (horse) =>
          !query ||
          horse.name?.toLowerCase().includes(query) ||
          horse.breed?.toLowerCase().includes(query) ||
          horse.ownerName?.toLowerCase().includes(query)
      );
  };

  const handleWeeklyDayToggle = (day) => {
    setFormData(prev => {
      const currentDays = prev.weeklyDays || [];
      const isSelected = currentDays.includes(day);
      const newDays = isSelected 
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      
      return {
        ...prev,
        weeklyDays: newDays
      };
    });
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Professional': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Weekly': return 'bg-blue-100 text-blue-800';
      case 'Daily': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTargetColor = (target) => {
    switch (target) {
      case 'Rider': return 'bg-green-100 text-green-800';
      case 'Owner': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5);
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  } 
*/

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('sessions.simplified.title')}</h1>
          <p className="mt-2 text-gray-600">
            {t('sessions.simplified.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex bg-gray-100 rounded-xl p-1 shadow-sm border border-gray-200 flex-1 sm:flex-none">
            <button
              onClick={() => setViewMode('weekly')}
              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                viewMode === 'weekly' ? 'bg-white text-primary-700 shadow-sm ring-1 ring-black/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Weekly Session
            </button>
            <button
              onClick={() => setViewMode('daily')}
              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                viewMode === 'daily' ? 'bg-white text-primary-700 shadow-sm ring-1 ring-black/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Daily Session
            </button>
          </div>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="inline-flex items-center justify-center w-full sm:w-auto px-5 py-2.5 bg-primary-600 text-white font-medium rounded-xl shadow-sm hover:shadow-md hover:bg-primary-700 hover:-translate-y-0.5 transition-all duration-200"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('sessions.simplified.createSession')}
          </button>
        </div>
          </div>


      {/* Sessions List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {viewMode === 'weekly' ? 'Weekly Sessions' : 
             viewMode === 'daily' ? 'Daily Sessions' : 
             'Calendar View'} ({viewMode === 'calendar' ? 'All Sessions' : sessions.length})
          </h3>
          </div>
        
        {viewMode === 'calendar' ? (
          <CalendarView
            currentDate={currentDate}
            sessions={sessions}
            onNavigateMonth={navigateMonth}
            onNavigateWeek={navigateWeek}
            onNavigateDay={navigateDay}
            onGoToToday={goToToday}
            onEditSession={handleEdit}
            onManageParticipants={handleManageParticipants}
            onDeleteSession={handleDeleteSession}
            formatTime={formatTime}
            getLevelColor={getLevelColor}
            getTypeColor={getTypeColor}
            getTargetColor={getTargetColor}
          />
        ) : (
          <div className="divide-y divide-gray-200">
          {sessions.map((session) => (
            <div key={session.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
              <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
                <div className="flex-1 w-full">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 w-full sm:w-auto mb-1 sm:mb-0">
                      {session.name || `${session.level} Level Session`}
                    </h4>
                    {session.stable && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t(`sessions.simplified.form.stable${session.stable}`)}
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(session.level)}`}>
                      {session.levelName || t(`sessions.simplified.form.${session.level?.toLowerCase()}`)}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(session.type)}`}>
                      {session.typeName || t(`sessions.simplified.form.${session.type?.toLowerCase()}`)}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTargetColor(session.target)}`}>
                      {session.targetName || t(`sessions.simplified.form.${session.target?.toLowerCase()}s`)}
                    </span>
                    {session.type === 0 && session.weeklyDays && !isIsoDateList(session.weeklyDays) && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-200 text-purple-900 border border-purple-300">
                        📅 {session.weeklyDays.split(',').map(day => t(`sessions.daysOfWeek.${day.toLowerCase()}`)).join(', ')}
                      </span>
                    )}
                    {session.type === 1 && session.weeklyDays && isIsoDateList(session.weeklyDays) && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-200 text-purple-900 border border-purple-300">
                        📅 {session.weeklyDays.split(',').map((d) => new Date(`${d.trim()}T12:00:00`).toLocaleDateString()).join(' · ')}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {session.type !== 0 && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{new Date(session.date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                      </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-2" />
                      <span>{formatSessionParticipantLabel(session)}</span>
                      </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="text-lg mr-2">🐎</span>
                      <span>
                        {session.horses && session.horses.length > 0 
                          ? `${session.horses.length} horse(s) assigned`
                          : 'No horses assigned'
                        }
                      </span>
                    </div>
                      </div>
                  
                  {/* Assigned Horses */}
                  {session.horses && session.horses.length > 0 && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-800 mb-2">
                        <span className="text-lg mr-2">🐎</span>
                        <span className="font-medium">Assigned Horses</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {session.horses.map((horse, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {horse.horseName}
                            {horse.riderName && ` (${horse.riderName})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Weekly session day display */}
                    {session.type === 0 && session.weeklyDays && (
                      <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center text-purple-800">
                          <Calendar className="w-5 h-5 mr-2" />
                          <span className="font-semibold">{t('sessions.types.weekly')} - {t('sessions.form.recurringEndDate')} {session.weeklyDays.split(',').map(day => t(`sessions.daysOfWeek.${day.trim().toLowerCase()}`)).join(', ')}</span>
                            </div>
                            </div>
                          )}
                  
                  {session.type === 1 && session.expiresAt && (
                    <div className="text-sm text-orange-600">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Ends after: {new Date(session.expiresAt).toLocaleDateString()}
                    </div>
                  )}
                        </div>
                
                <div className="flex flex-col w-full lg:w-auto lg:ml-6 space-y-2 shrink-0 mt-4 lg:mt-0 border-t lg:border-t-0 pt-4 lg:pt-0 border-gray-100">
                  <button
                    onClick={() => handleEdit(session)}
                    className="w-full inline-flex items-center justify-center lg:justify-start px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('sessions.simplified.editSession')}
                  </button>
                  <button
                    onClick={() => handleManageParticipants(session)}
                    className="w-full inline-flex items-center justify-center lg:justify-start px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-100 hover:bg-primary-100 rounded-lg transition-colors"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {t('sessions.simplified.manageParticipants')}
                  </button>
                  <button
                    onClick={() => handleManageHorses(session)}
                    className="w-full inline-flex items-center justify-center lg:justify-start px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-100 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <span className="text-lg mr-2">🐎</span>
                    Manage Horses
                  </button>
                  <button
                    onClick={() => handleDeleteSession(session.id)}
                    className="w-full inline-flex items-center justify-center lg:justify-start px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-100 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('sessions.simplified.deleteSession')}
                  </button>
                </div>
              </div>
            </div>
                ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('common.showing')} {((currentPage - 1) * itemsPerPage) + 1} {t('common.to')} {Math.min(currentPage * itemsPerPage, totalItems)} {t('common.of')} {totalItems} {t('common.results')}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.last')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Sessions */}
      {sessions.length === 0 && !loading && (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
          <p className="text-gray-500 mb-4">
            {viewMode === 'daily' 
              ? 'No daily sessions have been created yet.' 
              : 'No weekly sessions have been created yet.'}
          </p>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Session
          </button>
        </div>
      )}

      {/* Create / Edit Session Modal */}
      {showCreateModal && (() => {
        const { steps: wizardSteps, index: wizardIndex, title: wizardTitle } = getCurrentWizardMeta();
        const isEdit = !!editingSession;

        return (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-2xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className={`px-5 py-4 text-white shrink-0 ${isEdit ? 'bg-gradient-to-r from-amber-600 to-orange-600' : 'bg-gradient-to-r from-indigo-600 to-blue-600'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {isEdit ? <Edit className="w-5 h-5 shrink-0 opacity-90" /> : <Plus className="w-5 h-5 shrink-0 opacity-90" />}
                      <h3 className="text-lg font-semibold truncate">
                        {isEdit ? t('sessions.editSession') : t('sessions.createNewSession')}
                      </h3>
                    </div>
                    <p className="text-sm mt-1 opacity-90">
                      {t('common.step')} {wizardIndex + 1} {t('common.of')} {wizardSteps.length} · {wizardTitle}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                    aria-label="Close"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {/* Progress */}
                <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-1">
                  {wizardSteps.map((step, idx) => (
                    <React.Fragment key={step.step}>
                      <div
                        className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                          idx <= wizardIndex ? 'bg-white text-indigo-700' : 'bg-white/25 text-white'
                        }`}
                        title={step.label}
                      >
                        {idx + 1}
                      </div>
                      {idx < wizardSteps.length - 1 && (
                        <div className={`h-0.5 w-4 sm:w-8 shrink-0 ${idx < wizardIndex ? 'bg-white' : 'bg-white/30'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Step content */}
              <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5">

                {/* Step 1: Session Name & Stable */}
                {currentStep === 1 && (
                <div className="space-y-4">
<h4 className="text-lg font-medium text-gray-900">{t('sessions.sessionDetails')}</h4>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
{t('sessions.sessionName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
placeholder={t('sessions.sessionNamePlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
{t('sessions.stable')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.stable}
                        onChange={(e) => setFormData({...formData, stable: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
<option value="">{t('sessions.selectStable')}</option>
                        {stables.map((stable) => (
                          <option key={stable.value} value={stable.value}>
                            {stable.label}
                          </option>
                        ))}
                      </select>
                  </div>
                </div>
                )}

                {/* Step 2: Target (Riders or Owners) */}
                {currentStep === 2 && (
                  <div className="space-y-4">
<h4 className="text-lg font-medium text-gray-900">{t('sessions.whoIsThisSessionFor')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {sessionTargets.map((target) => (
                  <button
                          key={target.value}
                    type="button"
                          onClick={() => setFormData({...formData, target: target.value})}
                          className={`p-4 border-2 rounded-lg text-center transition-colors ${
                            formData.target === target.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">
                            {target.value === 'Rider' ? '🏇' : '👥'}
                      </div>
                          <div className="font-medium">{target.label}</div>
                  </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Rider Level (only if Riders selected) */}
                {currentStep === 3 && formData.target === 'Rider' && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Rider Level</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {riderLevels.map((level) => (
                  <button
                          key={level.value}
                    type="button"
                          onClick={() => setFormData({...formData, level: level.value})}
                          className={`p-4 border-2 rounded-lg text-left transition-colors ${
                            formData.level === level.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">{level.label}</div>
                          <div className="text-sm text-gray-600">
                            {level.value === 'Beginner' && 'New to horseback riding'}
                            {level.value === 'Intermediate' && 'Some experience with horses'}
                            {level.value === 'Professional' && 'Advanced riding skills'}
                          </div>
                  </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4: Session Type */}
                {currentStep === 4 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Session Type</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {sessionTypes.map((type) => (
                  <button
                          key={type.value}
                    type="button"
                          onClick={() => setFormData({...formData, type: type.value})}
                          className={`p-4 border-2 rounded-lg text-center transition-colors ${
                            formData.type === type.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">
                            {type.value === 'Weekly' ? '📅' : '📆'}
                      </div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-sm text-gray-600">
                            {type.value === 'Weekly' ? 'Repeats every week' : 'One-time session'}
                      </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5: Weekly Days or Date Selection */}
                {currentStep === 5 && (
                  <div className="space-y-4">
                    {formData.type === 'Weekly' ? (
                      <>
                        <h4 className="text-lg font-medium text-gray-900">Select Days of the Week</h4>
                        <p className="text-sm text-gray-600">Choose which days this session should repeat</p>
                        <p className="text-sm text-blue-600 font-medium">
                          Selected: {(formData.weeklyDays || []).length} day(s) - {(formData.weeklyDays || []).join(', ') || 'None'}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {weeklyDays.map((day) => (
                            <button
                              key={day.value}
                              type="button"
                              onClick={() => handleWeeklyDayToggle(day.value)}
                    className={`p-3 border-2 rounded-lg text-center transition-colors ${
                                (formData.weeklyDays || []).includes(day.value)
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {day.label}
                  </button>
                          ))}
                </div>
                      </>
                    ) : (
                      <>
                        <h4 className="text-lg font-medium text-gray-900">Select dates</h4>
                        <p className="text-sm text-gray-600">
                          Add each day this session runs (e.g. 12, 17 and 19 of the month). It is removed after the last date.
                        </p>
                        <p className="text-sm text-blue-600 font-medium">
                          Selected: {(formData.selectedDates || []).length} date(s)
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="date"
                            value={dailyDateDraft}
                            onChange={(e) => setDailyDateDraft(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={addDailyDate}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                          >
                            Add date
                          </button>
                        </div>
                        {(formData.selectedDates || []).length > 0 ? (
                          <ul className="space-y-2 max-h-48 overflow-y-auto">
                            {(formData.selectedDates || []).map((dateStr) => (
                              <li
                                key={dateStr}
                                className="flex items-center justify-between px-3 py-2 bg-purple-50 border border-purple-100 rounded-lg text-sm"
                              >
                                <span className="font-medium text-purple-900">
                                  {new Date(`${dateStr}T12:00:00`).toLocaleDateString(undefined, {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeDailyDate(dateStr)}
                                  className="text-red-600 hover:text-red-800 text-xs font-medium"
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">No dates selected yet.</p>
                        )}
                      </>
                    )}
              </div>
                )}

                {/* Step 6: Time Selection & Capacity */}
                {currentStep === 6 && (
                <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Time Selection & Capacity</h4>
                    <div className="grid grid-cols-2 gap-4">
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                          type="time"
                      required
                          value={formData.startTime}
                          onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time <span className="text-red-500">*</span>
                            </label>
                    <input
                          type="time"
                          required
                          value={formData.endTime}
                          onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                    
                    {/* Max Participants */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maximum Participants
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={formData.maxParticipants}
                          onChange={(e) => setFormData({...formData, maxParticipants: parseInt(e.target.value) || 10})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="10"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          {formData.useSlots
                            ? 'Maximum participants allowed per slot'
                            : 'Set the maximum number of participants allowed in this session'}
                        </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.useSlots}
                          onChange={(e) => setFormData({ ...formData, useSlots: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Enable time slots with pauses</span>
                      </label>

                      {formData.useSlots && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Slot duration (minutes)</label>
                            <input
                              type="number"
                              min="5"
                              max="180"
                              value={formData.slotDurationMinutes}
                              onChange={(e) => setFormData({ ...formData, slotDurationMinutes: parseInt(e.target.value) || 20 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Pause between slots (minutes)</label>
                            <input
                              type="number"
                              min="0"
                              max="60"
                              value={formData.slotPauseMinutes}
                              onChange={(e) => setFormData({ ...formData, slotPauseMinutes: parseInt(e.target.value) || 0 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="sm:col-span-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-3">
                            {calculateSlotCount(formData.startTime, formData.endTime, formData.slotDurationMinutes, formData.slotPauseMinutes)} slot(s) will be created.
                            Each slot allows up to {formData.maxParticipants} participant(s).
                            Total capacity: {calculateSlotCount(formData.startTime, formData.endTime, formData.slotDurationMinutes, formData.slotPauseMinutes) * formData.maxParticipants}.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>

              {/* Footer navigation */}
              <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 sm:px-5 py-3 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={wizardIndex === 0}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                    wizardIndex === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.previous')}
                </button>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    {t('common.cancel')}
                  </button>
                  {isLastWizardStep() ? (
                    <button
                      type="button"
                      onClick={isEdit ? handleEditSession : handleCreateSession}
                      className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                        isEdit ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isEdit
                        ? 'Update Session'
                        : `Create Session${formData.type === 'Weekly' && (formData.weeklyDays || []).length > 1 ? 's' : ''}`}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {t('common.next')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Participants Management Modal */}
      {showParticipantsModal && selectedSession && (() => {
        const usesSlots = sessionUsesSlots(selectedSession);
        const selectedSlot = getSelectedSlot();
        const assignableUsers = getAssignableUsersForTab();
        const accent = getParticipantAccent(assignUserTab);
        const accentBg = accent === 'purple' ? 'bg-purple-100' : accent === 'orange' ? 'bg-orange-100' : 'bg-blue-100';
        const accentText = accent === 'purple' ? 'text-purple-700' : accent === 'orange' ? 'text-orange-700' : 'text-blue-700';
        const totalNonCoaches = selectedSession.participants?.filter((p) => p.user?.role !== 2).length ?? 0;
        const coachCount = getCoachesInSession().length;
        const assignTabs = [
          {
            id: 'riders',
            label: selectedSession.target === 0 ? 'Riders' : 'Owners',
            count: getTargetUsers().length
          },
          { id: 'coaches', label: 'Coaches', count: getCoaches().length },
          { id: 'temporary', label: 'Temporary', count: getTemporaryUsers().length }
        ];

        return (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-4xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 text-white shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 shrink-0 opacity-90" />
                      <h3 className="text-lg font-semibold truncate">Manage Participants</h3>
                    </div>
                    <p className="text-blue-100 text-sm mt-1 truncate">{selectedSession.name}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full">
                        {coachCount} coach{coachCount !== 1 ? 'es' : ''}
                      </span>
                      <span className="text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full">
                        {totalNonCoaches} participant{totalNonCoaches !== 1 ? 's' : ''}
                      </span>
                      {usesSlots && (
                        <span className="text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full">
                          {sessionSlots.length} slot{sessionSlots.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowParticipantsModal(false)}
                    className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                    aria-label="Close"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {/* Main tabs */}
                <div className="flex gap-1 mt-4 bg-white/10 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setParticipantModalTab('assign')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      participantModalTab === 'assign' ? 'bg-white text-blue-700 shadow-sm' : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    <UserPlus className="w-4 h-4" />
                    Assign
                  </button>
                  <button
                    type="button"
                    onClick={() => setParticipantModalTab('overview')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      participantModalTab === 'overview' ? 'bg-white text-blue-700 shadow-sm' : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    <LayoutList className="w-4 h-4" />
                    Overview
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {participantModalTab === 'assign' ? (
                  <div className="p-4 sm:p-5 space-y-4">
                    {/* Slot picker */}
                    {usesSlots && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">Time slot</p>
                          {selectedSlot && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              isSlotFull(selectedSlot) ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {getSlotCurrent(selectedSlot)}/{getSlotMax(selectedSlot)} in slot
                            </span>
                          )}
                        </div>
                        {sessionSlots.length === 0 ? (
                          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                            No slots found. Edit the session, enable time slots, and save again.
                          </p>
                        ) : (
                          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                            {sessionSlots.map((slot) => {
                              const slotId = getSlotId(slot);
                              const selected = selectedSlotId === slotId;
                              const full = isSlotFull(slot);
                              return (
                                <button
                                  key={slotId}
                                  type="button"
                                  onClick={() => {
                                    setSelectedSlotId(slotId);
                                    setSelectedUsers([]);
                                  }}
                                  className={`shrink-0 min-w-[140px] p-3 rounded-xl border-2 text-left transition-all ${
                                    selected
                                      ? 'border-blue-600 bg-blue-50 shadow-sm ring-2 ring-blue-100'
                                      : full
                                        ? 'border-red-200 bg-red-50/80'
                                        : 'border-gray-200 bg-white hover:border-blue-300'
                                  }`}
                                >
                                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Slot {getSlotIndex(slot) + 1}
                                  </div>
                                  <div className="text-sm font-medium text-gray-900 mt-0.5">
                                    {formatSlotTime(slot.startTime ?? slot.StartTime)} – {formatSlotTime(slot.endTime ?? slot.EndTime)}
                                  </div>
                                  <div className={`text-xs mt-1 ${full ? 'text-red-600' : 'text-gray-500'}`}>
                                    {getSlotCurrent(slot)}/{getSlotMax(slot)}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {!selectedSlotId && sessionSlots.length > 0 && (
                          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                            Select a slot above before assigning riders or owners.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Capacity warning (non-slot sessions) */}
                    {!usesSlots && selectedSession.maxParticipants && getParticipantsInSession().length >= selectedSession.maxParticipants && (
                      <div className="flex gap-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
                        <span className="shrink-0 text-red-500">⚠</span>
                        <p>Session is full ({selectedSession.maxParticipants} max). Remove someone before adding more.</p>
                      </div>
                    )}

                    {/* Search + user type tabs */}
                    <div className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="search"
                          value={participantSearch}
                          onChange={(e) => setParticipantSearch(e.target.value)}
                          placeholder="Search by name or email…"
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                        {assignTabs.map((tab) => (
                          <button
                            key={tab.id}
                            type="button"
                            onClick={() => setAssignUserTab(tab.id)}
                            className={`flex-1 py-2 px-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                              assignUserTab === tab.id
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {tab.label}
                            <span className="ml-1 text-gray-400">({tab.count})</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* User list */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {loadingUsers ? (
                        <div className="py-12 text-center text-gray-500 text-sm">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3" />
                          Loading users…
                        </div>
                      ) : usesSlots && assignUserTab !== 'coaches' && !selectedSlotId ? (
                        <div className="py-12 text-center text-gray-500 text-sm px-4">
                          Pick a time slot to see available {assignUserTab === 'temporary' ? 'temporary users' : 'participants'}.
                        </div>
                      ) : assignableUsers.length === 0 ? (
                        <div className="py-12 text-center text-gray-500 text-sm px-4">
                          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          No {assignTabs.find((t) => t.id === assignUserTab)?.label.toLowerCase()} available
                          {participantSearch.trim() ? ' matching your search' : ''}.
                        </div>
                      ) : (
                        <ul className="divide-y divide-gray-100 max-h-[min(320px,40vh)] overflow-y-auto">
                          {assignableUsers.map((user) => {
                            const isSelected = selectedUsers.some((u) => u.id === user.id);
                            return (
                              <li key={user.id}>
                                <button
                                  type="button"
                                  onClick={() => handleUserSelectClick(user)}
                                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                    isSelected ? 'bg-green-50' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${accentBg}`}>
                                    <span className={`font-medium text-sm ${accentText}`}>
                                      {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                    </span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className={`font-medium truncate ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                                      {user.firstName} {user.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">{getRoleLabel(user.role)}</div>
                                  </div>
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                                    isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Overview tab */
                  <div className="p-4 sm:p-5 space-y-5">
                    {/* Coaches */}
                    <section>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500" />
                        Coaches ({coachCount})
                      </h4>
                      {coachCount === 0 ? (
                        <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">No coaches assigned yet.</p>
                      ) : (
                        <ul className="space-y-2">
                          {getCoachesInSession().map((participant) => (
                            <li
                              key={participant.id}
                              className="flex items-center justify-between p-3 bg-purple-50 border border-purple-100 rounded-lg"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                                  <span className="text-purple-700 text-sm font-medium">
                                    {participant.user.firstName.charAt(0)}{participant.user.lastName.charAt(0)}
                                  </span>
                                </div>
                                <span className="font-medium text-gray-900 truncate">
                                  {participant.user.firstName} {participant.user.lastName}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveParticipant(participant.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                                title={t('sessions.simplified.removeCoach')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    {/* Participants by slot or flat list */}
                    <section>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        Participants ({totalNonCoaches})
                      </h4>

                      {usesSlots ? (
                        sessionSlots.length === 0 ? (
                          <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">No slots configured.</p>
                        ) : (
                          <div className="space-y-4">
                            {sessionSlots.map((slot) => {
                              const slotId = getSlotId(slot);
                              const slotParticipants = (selectedSession.participants || []).filter(
                                (p) => p.user?.role !== 2 && getParticipantSlotId(p) === slotId
                              );
                              return (
                                <div key={slotId} className="border border-gray-200 rounded-xl overflow-hidden">
                                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                                    <span className="text-sm font-medium text-gray-800">
                                      Slot {getSlotIndex(slot) + 1} · {formatSlotTime(slot.startTime ?? slot.StartTime)} – {formatSlotTime(slot.endTime ?? slot.EndTime)}
                                    </span>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                      isSlotFull(slot) ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'
                                    }`}>
                                      {slotParticipants.length}/{getSlotMax(slot)}
                                    </span>
                                  </div>
                                  {slotParticipants.length === 0 ? (
                                    <p className="text-sm text-gray-400 py-4 text-center">Empty</p>
                                  ) : (
                                    <ul className="divide-y divide-gray-100">
                                      {slotParticipants.map((participant) => (
                                        <li key={participant.id} className="flex items-center justify-between px-4 py-2.5">
                                          <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                              <span className="text-blue-700 text-xs font-medium">
                                                {participant.user.firstName.charAt(0)}{participant.user.lastName.charAt(0)}
                                              </span>
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 truncate">
                                              {participant.user.firstName} {participant.user.lastName}
                                            </span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveParticipant(participant.id)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                                            title={t('sessions.simplified.removeParticipant')}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )
                      ) : totalNonCoaches === 0 ? (
                        <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">No participants assigned yet.</p>
                      ) : (
                        <ul className="space-y-2">
                          {getParticipantsInSession().map((participant) => (
                            <li
                              key={participant.id}
                              className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                  <span className="text-blue-700 text-sm font-medium">
                                    {participant.user.firstName.charAt(0)}{participant.user.lastName.charAt(0)}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 truncate">
                                    {participant.user.firstName} {participant.user.lastName}
                                  </div>
                                  <div className="text-xs text-gray-500">{getRoleLabel(participant.user.role)}</div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveParticipant(participant.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                                title={t('sessions.simplified.removeParticipant')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 sm:px-5 py-3 flex items-center justify-between gap-3">
                {participantModalTab === 'assign' && selectedUsers.length > 0 ? (
                  <>
                    <p className="text-sm text-gray-600 truncate">
                      <UserCheck className="w-4 h-4 inline mr-1 text-green-600" />
                      {selectedUsers.length} selected
                    </p>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setSelectedUsers([])}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmAssignments}
                        disabled={!canAssignUsers()}
                        className="px-5 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Assign ({selectedUsers.length})
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 hidden sm:block">
                      {participantModalTab === 'assign'
                        ? 'Select people below, then assign with one click.'
                        : 'Remove participants from the overview list.'}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowParticipantsModal(false)}
                      className="ml-auto px-5 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Horse Management Modal */}
      {showHorsesModal && selectedSession && (() => {
        const assignedCount = Array.isArray(sessionHorses) ? sessionHorses.length : 0;
        const availableHorses = getAvailableSessionHorses();

        return (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-3xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4 text-white shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xl shrink-0">🐎</span>
                      <h3 className="text-lg font-semibold truncate">Manage Horses</h3>
                    </div>
                    <p className="text-emerald-100 text-sm mt-1 truncate">{selectedSession.name}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full">
                        {assignedCount} assigned
                      </span>
                      <span className="text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full">
                        {availableHorses.length} available
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowHorsesModal(false)}
                    className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                    aria-label="Close"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex gap-1 mt-4 bg-white/10 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setHorseModalTab('assign')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      horseModalTab === 'assign' ? 'bg-white text-emerald-700 shadow-sm' : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    Add horses
                  </button>
                  <button
                    type="button"
                    onClick={() => setHorseModalTab('overview')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      horseModalTab === 'overview' ? 'bg-white text-emerald-700 shadow-sm' : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    <LayoutList className="w-4 h-4" />
                    Assigned ({assignedCount})
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {horseModalTab === 'assign' ? (
                  <div className="p-4 sm:p-5 space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="search"
                        value={horseSearchTerm}
                        onChange={(e) => setHorseSearchTerm(e.target.value)}
                        placeholder="Search by name, breed, or owner…"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {availableHorses.length === 0 ? (
                        <div className="py-12 text-center text-gray-500 text-sm px-4">
                          <span className="text-3xl block mb-2">🐎</span>
                          {horseSearchTerm.trim()
                            ? 'No horses match your search.'
                            : assignedCount > 0
                              ? 'All horses are already assigned to this session.'
                              : 'No horses available. Add horses in the Horses section first.'}
                        </div>
                      ) : (
                        <ul className="divide-y divide-gray-100 max-h-[min(360px,45vh)] overflow-y-auto">
                          {availableHorses.map((horse) => (
                            <li key={horse.id}>
                              <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                                <span className="text-2xl shrink-0">🐎</span>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 truncate">{horse.name}</div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {horse.breed && `${horse.breed} · `}
                                    Owner: {horse.ownerName}
                                    {horse.riderName && ` · Rider: ${horse.riderName}`}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => assignHorseToSession(selectedSession.id, horse.id)}
                                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                                  title="Assign horse to session"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 sm:p-5">
                    {assignedCount === 0 ? (
                      <div className="py-12 text-center text-gray-500 text-sm bg-gray-50 rounded-xl">
                        <span className="text-3xl block mb-2">🐎</span>
                        No horses assigned yet. Switch to <strong>Add horses</strong> to assign some.
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {sessionHorses.map((horse) => (
                          <li
                            key={horse.id}
                            className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-xl"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-2xl shrink-0">🐎</span>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{horse.horseName}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {horse.horseBreed && `${horse.horseBreed} · `}
                                  Owner: {horse.ownerName}
                                  {horse.riderName && ` · Rider: ${horse.riderName}`}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeHorseFromSession(selectedSession.id, horse.horseId)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                              title="Remove horse from session"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 sm:px-5 py-3 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500 hidden sm:block">
                  {horseModalTab === 'assign'
                    ? 'Tap Add to assign a horse to this session.'
                    : 'Remove horses you no longer need for this session.'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowHorsesModal(false)}
                  className="ml-auto px-5 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

export default SimplifiedSessionManagement;
